import axios from "axios";
import type {
  AnalysisPendingResponse,
  AnalysisResponse,
  AnalysisStartResult,
  LocationSuggestion,
} from "./types";

// Endpoints (mirror packs/location_analysis/config/routes/location_analysis.rb)
export const ROUTES = {
  locations: "/outils/analyse-de-local/locations",
  analyse: "/outils/analyse-de-local/analyser",
} as const;

export async function searchLocations(
  query: string,
  signal?: AbortSignal,
): Promise<LocationSuggestion[]> {
  if (query.trim().length < 2) return [];

  const { data } = await axios.get<{ results: LocationSuggestion[] }>(
    ROUTES.locations,
    { params: { q: query }, signal },
  );
  return data.results ?? [];
}

/**
 * Kicks off the analyse pipeline. Two possible outcomes:
 *   * 200 + completed payload  → cache hit, caller renders directly.
 *   * 202 + { status: "pending", job_id } → cache miss; caller opens
 *     an ActionCable subscription on LocationAnalysis::AnalysisChannel
 *     keyed by `job_id` and waits for the broadcast.
 *
 * Any other 4xx/5xx (403 quota, 422 not-configured / ungeolocated,
 * 502 generic) throws as before — callers handle in their catch.
 */
export async function analyseLocation(
  locationId: number | string,
): Promise<AnalysisStartResult> {
  const resp = await axios.post<AnalysisResponse | AnalysisPendingResponse>(
    ROUTES.analyse,
    { location_id: locationId },
    {
      // Axios default validateStatus rejects 4xx/5xx. We KEEP that for
      // 4xx/5xx (so the caller's catch fires) but explicitly allow
      // 202 alongside 200/201 so it doesn't reject.
      validateStatus: (status) => status >= 200 && status < 300,
    },
  );

  if (resp.status === 202 && (resp.data as AnalysisPendingResponse).status === "pending") {
    const pending = resp.data as AnalysisPendingResponse;
    return {
      kind: "pending",
      jobId: pending.job_id,
      location: pending.location,
    };
  }

  return { kind: "completed", response: resp.data as AnalysisResponse };
}
